/**
 * Template Renderer — renders broadcast graphics as HTML inside a container
 * Uses absolutely-positioned HTML/CSS elements, scaled via CSS transform
 */
import type { TemplateLayer, TemplateField, ProjectItem } from '@graphicsim/shared-types';
import { templateRegistry } from './templateRegistry';
import { projectStore } from '../stores/projectStore';

const DESIGN_WIDTH = 1280;
const DESIGN_HEIGHT = 720;

export interface ItemTransform {
  posX: number;    // position offset in % of container width
  posY: number;    // position offset in % of container height
  scaleX: number;  // percentage (100 = 100%)
  scaleY: number;
  fitMode: 'fill' | 'fit' | 'stretch';
}

const DEFAULT_TRANSFORM: ItemTransform = { posX: 0, posY: 0, scaleX: 100, scaleY: 100, fitMode: 'fit' };

/**
 * Compute CSS scale values based on design size, container size, fit mode, and user scale.
 */
function computeScale(
  containerW: number, containerH: number,
  designW: number, designH: number,
  tx: ItemTransform,
): { sx: number; sy: number } {
  const ratioX = containerW / designW;
  const ratioY = containerH / designH;
  let sx: number, sy: number;
  switch (tx.fitMode) {
    case 'fill':
      sx = sy = Math.max(ratioX, ratioY);
      break;
    case 'stretch':
      sx = ratioX;
      sy = ratioY;
      break;
    case 'fit':
    default:
      sx = sy = Math.min(ratioX, ratioY);
      break;
  }
  // Apply user scale on top
  sx *= tx.scaleX / 100;
  sy *= tx.scaleY / 100;
  return { sx, sy };
}

// ── SVG URL cache ──────────────────────────────────────────
const svgCache = new Map<string, string>();

/** Preload all SVG URL templates so they're available synchronously at render time */
export async function preloadSvgTemplates(): Promise<void> {
  const templates = templateRegistry.getAllTemplates().filter((t) => t.svgUrl);
  await Promise.all(
    templates.map(async (t) => {
      try {
        const resp = await fetch(t.svgUrl!);
        svgCache.set(t.svgUrl!, await resp.text());
      } catch (e) {
        console.warn(`Failed to preload SVG: ${t.svgUrl}`, e);
      }
    }),
  );
}

/**
 * Render a project item's template into a container element.
 * @param preview If true, videos show first frame paused instead of auto-playing.
 */
export function renderTemplate(container: HTMLElement, itemId: string, preview = false): void {
  const item = projectStore.getState().getItem(itemId);
  if (!item) {
    container.innerHTML = '';
    return;
  }

  const tx: ItemTransform = {
    posX: item.posX ?? 0,
    posY: item.posY ?? 0,
    scaleX: item.scaleX ?? 100,
    scaleY: item.scaleY ?? 100,
    fitMode: item.fitMode ?? 'fit',
  };

  // Media items (no templateId) — render image/placeholder directly
  if (!item.templateId) {
    renderMediaItem(container, item, tx, preview);
    return;
  }

  const template = templateRegistry.getTemplate(item.templateId);
  if (!template) {
    container.innerHTML = '';
    return;
  }

  const svgContent = template.svgContent || (template.svgUrl ? svgCache.get(template.svgUrl) : undefined);
  if (svgContent) {
    renderSvgTemplate(container, svgContent, item.fieldValues, template.width, template.height, tx);
  } else if (template.svgUrl) {
    // Not cached yet — fetch on demand and re-render
    container.innerHTML = '<div style="color:#666;font-size:11px;text-align:center;padding:10px;">Loading...</div>';
    fetch(template.svgUrl)
      .then((r) => r.text())
      .then((text) => {
        svgCache.set(template.svgUrl!, text);
        renderSvgTemplate(container, text, item.fieldValues, template.width, template.height, tx);
      });
  } else {
    renderLayers(container, template.layers, item.fieldValues, template.width, template.height, tx);
  }
}

/**
 * Render template layers into a container with proper scaling
 */
export function renderLayers(
  container: HTMLElement,
  layers: TemplateLayer[],
  fieldValues: Record<string, string>,
  width = DESIGN_WIDTH,
  height = DESIGN_HEIGHT,
  tx: ItemTransform = DEFAULT_TRANSFORM
): void {
  // Set up the scaled container
  container.classList.add('template-viewport');

  // Calculate scale using fit mode + user scale
  const containerRect = container.getBoundingClientRect();
  const { sx, sy } = computeScale(containerRect.width, containerRect.height, width, height, tx);

  // Create inner wrapper for the design space
  let inner = container.querySelector('.template-inner') as HTMLElement;
  if (!inner) {
    inner = document.createElement('div');
    inner.className = 'template-inner';
    container.innerHTML = '';
    container.appendChild(inner);
  }
  inner.style.width = `${width}px`;
  inner.style.height = `${height}px`;
  inner.style.transform = `scale(${sx}, ${sy})`;
  inner.style.transformOrigin = 'top left';
  // Store transform data for video board rescaling
  inner.dataset.fitMode = tx.fitMode;
  inner.dataset.userScaleX = String(tx.scaleX);
  inner.dataset.userScaleY = String(tx.scaleY);
  inner.dataset.posX = String(tx.posX);
  inner.dataset.posY = String(tx.posY);
  // Center if container is larger, then apply user position offset
  const offsetX = (containerRect.width - width * sx) / 2 + (tx.posX / 100) * containerRect.width;
  const offsetY = (containerRect.height - height * sy) / 2 + (tx.posY / 100) * containerRect.height;
  inner.style.left = `${offsetX}px`;
  inner.style.top = `${offsetY}px`;

  // Render each layer
  inner.innerHTML = layers
    .filter((l) => l.visible !== false)
    .map((layer) => renderLayer(layer, fieldValues))
    .join('');
}

function renderLayer(layer: TemplateLayer, fieldValues: Record<string, string>): string {
  const style = buildLayerStyle(layer, fieldValues);

  switch (layer.type) {
    case 'rect':
      return `<div class="tpl-layer tpl-rect" style="${style}"></div>`;

    case 'text': {
      const content = resolveFieldRef(layer.content || '', fieldValues);
      const textStyle = buildTextStyle(layer, fieldValues);
      return `<div class="tpl-layer tpl-text" style="${style}${textStyle}">${escapeHtml(content)}</div>`;
    }

    case 'image': {
      const src = resolveFieldRef(layer.src || '', fieldValues);
      return `<div class="tpl-layer tpl-image" style="${style}">
        ${src ? `<img src="${escapeAttr(src)}" style="width:100%;height:100%;object-fit:contain;" />` : ''}
      </div>`;
    }

    case 'group':
      return `<div class="tpl-layer tpl-group" style="${style}">
        ${(layer.children || []).map((child) => renderLayer(child, fieldValues)).join('')}
      </div>`;

    default:
      return '';
  }
}

function buildLayerStyle(layer: TemplateLayer, fieldValues: Record<string, string>): string {
  let style = `position:absolute;left:${layer.x}px;top:${layer.y}px;width:${layer.width}px;height:${layer.height}px;`;

  if (layer.fill) {
    style += `background:${resolveFieldRef(layer.fill, fieldValues)};`;
  }
  if (layer.stroke) {
    style += `border:${layer.strokeWidth || 1}px solid ${resolveFieldRef(layer.stroke, fieldValues)};`;
  }
  if (layer.borderRadius) {
    style += `border-radius:${layer.borderRadius}px;`;
  }
  if (layer.opacity !== undefined) {
    style += `opacity:${layer.opacity};`;
  }
  if (layer.transform) {
    style += `transform:${layer.transform};`;
  }
  if (layer.boxShadow) {
    style += `box-shadow:${layer.boxShadow};`;
  }

  return style;
}

function buildTextStyle(layer: TemplateLayer, fieldValues: Record<string, string>): string {
  let style = '';
  if (layer.fontSize) style += `font-size:${layer.fontSize}px;`;
  if (layer.fontFamily) style += `font-family:${layer.fontFamily};`;
  if (layer.fontWeight) style += `font-weight:${layer.fontWeight};`;
  if (layer.textAlign) style += `text-align:${layer.textAlign};`;
  if (layer.textColor) style += `color:${resolveFieldRef(layer.textColor, fieldValues)};`;
  if (layer.letterSpacing) style += `letter-spacing:${layer.letterSpacing}px;`;
  if (layer.textTransform) style += `text-transform:${layer.textTransform};`;
  if (layer.lineHeight) style += `line-height:${layer.lineHeight};`;
  // Vertically center text
  style += 'display:flex;align-items:center;';
  if (layer.textAlign === 'center') style += 'justify-content:center;';
  else if (layer.textAlign === 'right') style += 'justify-content:flex-end;';
  return style;
}

/**
 * Resolve {fieldId} references in a string
 */
function resolveFieldRef(value: string, fieldValues: Record<string, string>): string {
  return value.replace(/\{(\w+)\}/g, (_, fieldId) => fieldValues[fieldId] ?? '');
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ============================================================
// Shared Video — one <video> per URL, canvas mirrors for all views
// ============================================================

const sharedVideos = new Map<string, HTMLVideoElement>();

function getSharedVideo(url: string): HTMLVideoElement {
  let video = sharedVideos.get(url);
  if (!video) {
    console.debug('[SharedVideo] Creating new <video> for:', url);
    video = document.createElement('video');
    video.src = url;
    video.autoplay = true;
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.style.display = 'none';
    document.body.appendChild(video);

    let stallCount = 0;
    const MAX_RETRIES = 3;

    video.addEventListener('loadstart', () => console.debug('[SharedVideo] loadstart', url));
    video.addEventListener('loadedmetadata', () => {
      console.debug('[SharedVideo] loadedmetadata', url, `${video!.videoWidth}x${video!.videoHeight}`);
      stallCount = 0; // reset on success
    });
    video.addEventListener('loadeddata', () => console.debug('[SharedVideo] loadeddata readyState=', video!.readyState, url));
    video.addEventListener('canplay', () => console.debug('[SharedVideo] canplay', url));
    video.addEventListener('playing', () => {
      console.debug('[SharedVideo] playing', url);
      stallCount = 0;
    });
    video.addEventListener('waiting', () => console.debug('[SharedVideo] waiting (buffering)', url));
    video.addEventListener('stalled', () => {
      stallCount++;
      console.warn(`[SharedVideo] stalled (${stallCount}/${MAX_RETRIES}) — retrying`, url);
      if (stallCount <= MAX_RETRIES) {
        // Re-trigger load by resetting src
        const src = video!.src;
        video!.src = '';
        video!.src = src;
        video!.load();
        video!.play().catch(() => {});
      } else {
        console.error('[SharedVideo] Giving up after', MAX_RETRIES, 'stalls', url);
      }
    });
    video.addEventListener('error', () => {
      const e = video!.error;
      console.error('[SharedVideo] ERROR code=', e?.code, 'message=', e?.message, url);
    });
    video.addEventListener('suspend', () => console.debug('[SharedVideo] suspend', url));

    video.play().catch((err) => console.warn('[SharedVideo] play() rejected:', err.message, url));
    sharedVideos.set(url, video);
  } else {
    console.debug('[SharedVideo] Reusing existing <video> readyState=', video.readyState, 'paused=', video.paused, 'currentTime=', video.currentTime.toFixed(2), url);
  }
  return video;
}

/** Restart a shared video from the beginning */
export function restartSharedVideo(url: string): void {
  const video = sharedVideos.get(url);
  if (video) {
    console.debug('[SharedVideo] restart currentTime=0, play()', url);
    video.currentTime = 0;
    video.play().catch((err) => console.warn('[SharedVideo] restart play() rejected:', err.message, url));
  } else {
    console.warn('[SharedVideo] restart — no video found for', url);
  }
}

/** Get currentTime for a shared video by URL (used for cross-tab sync) */
export function getSharedVideoTime(url: string): number {
  const video = sharedVideos.get(url);
  return video ? video.currentTime : 0;
}

/** Get all shared video URLs (used for sync heartbeat) */
export function getSharedVideoUrls(): string[] {
  return Array.from(sharedVideos.keys());
}

/**
 * Create a <canvas> that mirrors the shared video source.
 * All canvases for the same URL show identical frames — zero delay.
 * The draw loop stops automatically when the canvas leaves the DOM.
 */
export function createSyncedVideo(url: string): HTMLCanvasElement {
  const source = getSharedVideo(url);
  const canvas = document.createElement('canvas');
  canvas.dataset.videoSrc = url; // preserve URL for cross-tab serialization
  canvas.style.cssText = 'width:100%;height:100%;background:#000;display:block;';

  let sized = false;
  let frameCount = 0;
  let lastLogTime = 0;

  function draw(): void {
    if (!canvas.isConnected) {
      console.debug('[SyncedVideo] Canvas disconnected from DOM, stopping draw loop', url);
      return; // auto-stop when removed from DOM
    }
    if (source.readyState >= 2) {
      if (!sized) {
        canvas.width = source.videoWidth || 640;
        canvas.height = source.videoHeight || 360;
        sized = true;
        console.debug('[SyncedVideo] Sized canvas to', canvas.width, 'x', canvas.height, url);
      }
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
      frameCount++;
    } else {
      // Log periodically when source isn't ready
      const now = performance.now();
      if (now - lastLogTime > 2000) {
        console.warn('[SyncedVideo] Source not ready — readyState=', source.readyState, 'paused=', source.paused, 'networkState=', source.networkState, url);
        lastLogTime = now;
      }
    }
    requestAnimationFrame(draw);
  }

  // Log after 3s if no frames drawn
  setTimeout(() => {
    if (frameCount === 0 && canvas.isConnected) {
      console.error('[SyncedVideo] 0 frames drawn after 3s — readyState=', source.readyState, 'paused=', source.paused, 'error=', source.error?.message, url);
    } else if (canvas.isConnected) {
      console.debug('[SyncedVideo] OK:', frameCount, 'frames drawn in 3s', url);
    }
  }, 3000);

  requestAnimationFrame(draw);
  return canvas;
}

// ============================================================
// Media Item Rendering (images, video placeholders, etc.)
// ============================================================

function renderMediaItem(container: HTMLElement, item: ProjectItem, tx: ItemTransform = DEFAULT_TRANSFORM, preview = false): void {
  container.classList.add('template-viewport');

  const url = item.mediaUrl;
  const userScaleX = (tx.scaleX ?? 100) / 100;
  const userScaleY = (tx.scaleY ?? 100) / 100;
  const fitMap = { fill: 'cover', fit: 'contain', stretch: 'fill' } as const;
  const objectFit = fitMap[tx.fitMode] || 'contain';
  const posOffset = (tx.posX !== 0 || tx.posY !== 0)
    ? `position:relative;left:${tx.posX}%;top:${tx.posY}%;`
    : '';
  const scaleStyle = (userScaleX !== 1 || userScaleY !== 1)
    ? `transform:scale(${userScaleX},${userScaleY});transform-origin:center;`
    : '';

  // Video items
  if (item.mediaType === 'video' && url) {
    container.innerHTML = '';
    if (preview) {
      // Preview mode: show first frame paused (used by project thumbnails)
      console.debug('[renderMediaItem] Preview mode (paused first frame) for', item.name, url);
      const video = document.createElement('video');
      video.src = url;
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      video.style.cssText = `width:100%;height:100%;object-fit:${objectFit};display:block;background:#000;${posOffset}${scaleStyle}`;
      video.addEventListener('loadeddata', () => {
        console.debug('[renderMediaItem] Preview video loadeddata — seeking to frame 0', item.name);
        video.currentTime = 0;
      }, { once: true });
      video.addEventListener('error', () => {
        console.error('[renderMediaItem] Preview video ERROR', video.error?.code, video.error?.message, url);
      });
      container.appendChild(video);
    } else {
      // Live mode: synced looping playback via shared <video> + canvas mirror
      console.debug('[renderMediaItem] Live mode (createSyncedVideo) for', item.name, url);
      const video = createSyncedVideo(url);
      video.style.objectFit = objectFit;
      if (posOffset || scaleStyle) video.style.cssText += posOffset + scaleStyle;
      container.appendChild(video);
    }
    return;
  }

  const isImage = item.mediaType === 'image' || item.mediaType === 'layer';

  if (isImage && url) {
    container.innerHTML = `<img src="${escapeAttr(url)}" style="width:100%;height:100%;object-fit:${objectFit};display:block;${posOffset}${scaleStyle}" alt="${escapeAttr(item.name)}" />`;
  } else {
    // Placeholder for AE, shot-layout, or missing URL
    const icon = getMediaIcon(item.mediaType);
    container.innerHTML = `<div style="width:100%;height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;color:#707070;font-size:11px;${posOffset}">
      <span style="font-size:24px;margin-bottom:4px;">${icon}</span>
      <span>${escapeHtml(item.name)}</span>
    </div>`;
  }
}

function getMediaIcon(mediaType?: string): string {
  switch (mediaType) {
    case 'image':
    case 'layer':
      return '&#x1F5BC;'; // framed picture
    case 'video':
      return '&#x1F3AC;'; // clapper board
    case 'ae':
      return '&#x1F39E;'; // film frames
    case 'shot-layout':
      return '&#x1F4CB;'; // clipboard
    default:
      return '&#x1F4C4;'; // page facing up
  }
}

// ============================================================
// SVG Template Rendering
// ============================================================

/**
 * Render an SVG-based template into a container with field value substitution
 */
function renderSvgTemplate(
  container: HTMLElement,
  svgContent: string,
  fieldValues: Record<string, string>,
  width: number,
  height: number,
  tx: ItemTransform = DEFAULT_TRANSFORM,
): void {
  container.classList.add('template-viewport');

  const containerRect = container.getBoundingClientRect();
  const { sx, sy } = computeScale(containerRect.width, containerRect.height, width, height, tx);

  let inner = container.querySelector('.template-inner') as HTMLElement;
  if (!inner) {
    inner = document.createElement('div');
    inner.className = 'template-inner';
    container.innerHTML = '';
    container.appendChild(inner);
  }
  inner.style.width = `${width}px`;
  inner.style.height = `${height}px`;
  inner.style.transform = `scale(${sx}, ${sy})`;
  inner.style.transformOrigin = 'top left';
  // Store transform data for video board rescaling
  inner.dataset.fitMode = tx.fitMode;
  inner.dataset.userScaleX = String(tx.scaleX);
  inner.dataset.userScaleY = String(tx.scaleY);
  inner.dataset.posX = String(tx.posX);
  inner.dataset.posY = String(tx.posY);
  const offsetX = (containerRect.width - width * sx) / 2 + (tx.posX / 100) * containerRect.width;
  const offsetY = (containerRect.height - height * sy) / 2 + (tx.posY / 100) * containerRect.height;
  inner.style.left = `${offsetX}px`;
  inner.style.top = `${offsetY}px`;

  // Inject SVG into the inner wrapper
  inner.innerHTML = svgContent;

  const svgEl = inner.querySelector('svg');
  if (!svgEl) return;

  svgEl.style.width = '100%';
  svgEl.style.height = '100%';

  // Update text fields via data-field attributes
  svgEl.querySelectorAll('[data-field]').forEach((el) => {
    const fieldId = el.getAttribute('data-field')!;
    if (fieldId === 'downDistance') {
      // Composite: combine down + distance fields
      const down = fieldValues['down'] || '1';
      const distance = fieldValues['distance'] || '10';
      el.textContent = `${getDownOrdinal(down)} & ${distance}`;
    } else if (fieldValues[fieldId] !== undefined) {
      el.textContent = fieldValues[fieldId].toUpperCase();
    }
  });

  // Update fill colors via data-fill attributes
  svgEl.querySelectorAll('[data-fill]').forEach((el) => {
    const fieldId = el.getAttribute('data-fill')!;
    if (fieldValues[fieldId] !== undefined) {
      // Use style.fill to override inline styles (higher specificity than attribute)
      (el as SVGElement).style.fill = fieldValues[fieldId];
    }
  });

  // Update image elements via data-image attributes
  svgEl.querySelectorAll('[data-image]').forEach((el) => {
    const fieldId = el.getAttribute('data-image')!;
    if (fieldValues[fieldId]) {
      el.setAttribute('href', fieldValues[fieldId]);
    }
  });

  // Toggle visibility of elements via data-toggle attributes
  svgEl.querySelectorAll('[data-toggle]').forEach((el) => {
    const fieldId = el.getAttribute('data-toggle')!;
    const val = fieldValues[fieldId];
    (el as SVGElement).style.display = (val === 'false' || val === '0' || val === '') ? 'none' : '';
  });

  // Auto-size text to fit within data-max-width bounds
  autoSizeSvgText(svgEl);
}

function getDownOrdinal(n: string): string {
  switch (n) {
    case '1': return '1ST';
    case '2': return '2ND';
    case '3': return '3RD';
    case '4': return '4TH';
    default: return n;
  }
}

/**
 * Auto-size SVG text elements that have a data-max-width attribute.
 * Measures rendered text width and uniformly scales down if it exceeds the max.
 */
/**
 * After renderTemplate places a scaled .template-inner inside a zone container,
 * re-compute the transform so only the contentBounds region fills the container.
 */
export function applyContentBoundsZoom(container: HTMLElement, templateId: string): void {
  const template = templateRegistry.getTemplate(templateId);
  if (!template?.contentBounds) return;

  const inner = container.querySelector<HTMLElement>('.template-inner');
  if (!inner) return;

  const bounds = template.contentBounds;
  const containerRect = container.getBoundingClientRect();
  if (containerRect.width === 0 || containerRect.height === 0) return;

  // Scale so content bounds fill the container
  const scaleX = containerRect.width / bounds.w;
  const scaleY = containerRect.height / bounds.h;
  const scale = Math.min(scaleX, scaleY);

  // Translate so content bounds origin maps to container top-left, then center
  const visibleW = bounds.w * scale;
  const visibleH = bounds.h * scale;
  const offsetX = (containerRect.width - visibleW) / 2 - bounds.x * scale;
  const offsetY = (containerRect.height - visibleH) / 2 - bounds.y * scale;

  inner.style.transform = `scale(${scale})`;
  inner.style.transformOrigin = 'top left';
  inner.style.left = `${offsetX}px`;
  inner.style.top = `${offsetY}px`;
}

function autoSizeSvgText(svgEl: SVGSVGElement): void {
  svgEl.querySelectorAll<SVGTextElement>('text[data-max-width]').forEach((textEl) => {
    const maxWidth = parseFloat(textEl.getAttribute('data-max-width')!);
    if (!maxWidth || maxWidth <= 0) return;

    try {
      const actualWidth = textEl.getComputedTextLength();
      if (actualWidth > maxWidth && actualWidth > 0) {
        const scaleFactor = maxWidth / actualWidth;
        // Append uniform scale to the existing transform
        const existing = textEl.getAttribute('transform') || '';
        textEl.setAttribute('transform', `${existing} scale(${scaleFactor})`);
      }
    } catch {
      // getComputedTextLength may throw if element not rendered yet
    }
  });
}
